import { useEffect, useRef, useState } from 'react';
import type { AdminBuild, AdminBuildList } from '@boardflight/shared';
import { api, download, fetchFile } from '../api.js';
import { Async, ListMore, Modal, Panel, Time, useAsync, useListLength } from '../ui.js';

/**
 * A game's builds (`docs/admin-plan.md` §D5, *A game's builds*): every finished build on every
 * channel, to download and install by hand, and a web build to play here.
 *
 * Each press is audited by the Worker before the bytes leave, so a download is one request and is
 * never fetched twice to show a size or a name.
 */
export function GameBuilds({ appId }: { appId: string }) {
  const [shown, more] = useListLength(appId);
  const list = useAsync(() => api<AdminBuildList>(`/v1/admin/apps/${appId}/builds?limit=${shown}`), [appId, shown]);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [previewing, setPreviewing] = useState<AdminBuild | null>(null);

  async function save(build: AdminBuild, bytes: 'published' | 'original') {
    setError(null);
    setBusy(`${build.id}:${bytes}`);
    try {
      await download(`/v1/admin/builds/${build.id}/file?bytes=${bytes}&for=download`);
    } catch (failure) {
      setError(String(failure));
    } finally {
      setBusy(null);
    }
  }

  return (
    <Panel title="Builds">
      <Async state={list} empty="No builds.">
        {(data) =>
          data.items.length === 0 ? (
            <p className="muted">No finished builds.</p>
          ) : (
            <>
              <div className="table-wrap">
                <table>
                  <thead>
                    <tr>
                      <th>Build</th>
                      <th>Channel</th>
                      <th className="num">Size</th>
                      <th>Finished</th>
                      <th />
                    </tr>
                  </thead>
                  <tbody>
                    {data.items.map((build) => (
                      <tr key={build.id}>
                        <td>
                          {build.buildNumber !== null ? <span className="muted">#{build.buildNumber} </span> : null}
                          <span className="mono">{build.versionName}</span>
                          <span className="muted"> ({build.versionCode})</span>
                          {build.patched ? <span className="pill" title="The feedback layer patched it; its developer's own bytes download separately">patched</span> : null}
                        </td>
                        <td>{build.channel.name}</td>
                        <td className="num" title={`sha256 ${build.sha256}`}>{size(build.size)}</td>
                        <td><Time at={build.readyAt ?? build.createdAt} /></td>
                        <td>
                          {build.expired ? (
                            <span className="muted">Past retention</span>
                          ) : (
                            <div className="row">
                              {data.platform === 'web' && data.previewOrigin ? (
                                <button onClick={() => setPreviewing(build)}>Preview</button>
                              ) : null}
                              <button disabled={busy !== null} onClick={() => void save(build, 'published')}>
                                {busy === `${build.id}:published` ? 'Downloading…' : 'Download'}
                              </button>
                              {build.patched ? (
                                <button disabled={busy !== null} onClick={() => void save(build, 'original')}>
                                  {busy === `${build.id}:original` ? 'Downloading…' : 'Original'}
                                </button>
                              ) : null}
                            </div>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <ListMore state={list} shown={shown} noun="builds" onMore={more} />
            </>
          )
        }
      </Async>
      {error ? <p className="error">{error}</p> : null}
      {previewing && list.data?.previewOrigin ? (
        <Preview build={previewing} origin={list.data.previewOrigin} onClose={() => setPreviewing(null)} />
      ) : null}
    </Panel>
  );
}

function size(bytes: number): string {
  if (bytes < 1024 * 1024) return `${Math.max(1, Math.round(bytes / 1024))} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(bytes < 100 * 1024 * 1024 ? 1 : 0)} MB`;
}

/** Board's screen, which the frame is laid out at and then scaled to fit. */
const BOARD = { width: 1920, height: 1080 };

/**
 * A web build running in the preview's own origin (`src/preview-host.ts`).
 *
 * The console fetches the build's original bytes with its session, frames the preview host's shell,
 * and posts the bytes in once the shell says it is ready. The game never runs on this origin, and
 * the frame never sees the session. Restart sends the same frame back to the shell, which says it is
 * ready again and is handed the same bytes, so it is not a second download.
 */
function Preview({ build, origin, onClose }: { build: AdminBuild; origin: string; onClose: () => void }) {
  const frame = useRef<HTMLIFrameElement>(null);
  const stage = useRef<HTMLDivElement>(null);
  const bytes = useRef<Promise<Blob> | null>(null);
  const [scale, setScale] = useState(0.5);
  const [state, setState] = useState<{ phase: 'loading' | 'running' | 'failed'; message?: string }>({ phase: 'loading' });

  useEffect(() => {
    const element = stage.current;
    if (!element) return;
    const fit = () => setScale(Math.min(element.clientWidth / BOARD.width, element.clientHeight / BOARD.height));
    fit();
    const observer = new ResizeObserver(fit);
    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    // Once per preview, here rather than in render, so it is one request and one audit row.
    bytes.current ??= fetchFile(`/v1/admin/builds/${build.id}/file?bytes=original&for=preview`).then((file) => file.blob);
    setState({ phase: 'loading' });
    const onMessage = (event: MessageEvent) => {
      if (event.origin !== origin || event.source !== frame.current?.contentWindow) return;
      const message = event.data as { type?: string; message?: string };
      if (message?.type === 'bf-preview-ready') {
        void (async () => {
          try {
            const buffer = await (await bytes.current!).arrayBuffer();
            frame.current?.contentWindow?.postMessage({ type: 'bf-preview-run', bytes: buffer }, origin, [buffer]);
            setState({ phase: 'running' });
          } catch (failure) {
            setState({ phase: 'failed', message: String(failure) });
          }
        })();
      } else if (message?.type === 'bf-preview-failed') {
        setState({ phase: 'failed', message: message.message ?? 'The build did not open.' });
      }
    };
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, [origin, build.id]);

  function restart() {
    setState({ phase: 'loading' });
    if (frame.current) frame.current.src = `${origin}/play/`;
  }

  return (
    <Modal
      full
      title={`${build.versionName}${build.buildNumber !== null ? ` · #${build.buildNumber}` : ''} · ${build.channel.name}`}
      onClose={onClose}
      actions={
        <>
          <button onClick={restart}>Restart</button>
          <button onClick={() => void stage.current?.requestFullscreen()}>Full screen</button>
        </>
      }
      subhead={
        <p className="note">
          {state.phase === 'failed' ? (
            <span className="error">{state.message}</span>
          ) : state.phase === 'loading' ? (
            'Loading the build…'
          ) : (
            <>The developer&rsquo;s own bytes, in a desktop browser at Board&rsquo;s size: no pieces, and nothing about how it runs on a Board.</>
          )}
        </p>
      }
    >
      <div ref={stage} className="preview-stage">
        <div className="preview-frame" style={{ width: BOARD.width * scale, height: BOARD.height * scale }}>
          <iframe
            ref={frame}
            title="Build preview"
            src={`${origin}/play/`}
            width={BOARD.width}
            height={BOARD.height}
            style={{ transform: `scale(${scale})` }}
            sandbox="allow-scripts allow-same-origin allow-pointer-lock allow-forms"
            allow="fullscreen; autoplay; gamepad"
            referrerPolicy="no-referrer"
          />
        </div>
      </div>
    </Modal>
  );
}
